import { useRecipientSelectRecipientsQuery } from 'schema'
import { SEPARATOR } from '../../types'
import { useOptions } from '../../../hooks/useOptions'
import { DefaultQueryHookOptions } from 'utils/defaults'
import {
  UserSelectLabelUser,
  useUserSelectLabel,
} from '../../UserSelect/hooks/useUserSelectLabel'
import {
  UserSelectTooltipUser,
  useUserSelectTooltip,
} from '../../UserSelect/hooks/useUserSelectTooltip'
import { NonUserEmailOption, Recipient, RecipientSelectContext } from './types'
import { useUserDeleted } from 'hooks/useUserDeleted'
import { createStrictContext } from 'utils/createContext'
import { toLabeledValues } from '../../../utils/labeledValues'
import { alphabeticalCompare } from '@cims-online/shared'
import { useProjectContext } from 'context/ProjectContext'
import type { LabeledValue } from 'antd/lib/select'

const { Provider, useContextFn } = createStrictContext<RecipientSelectContext>(
  'RecipientSelectContext'
)

export const useRecipientSelectContext = useContextFn

/**
 * A provider to give access to data and methods associated with the
 * RecipientSelect.
 *
 * This is designed to work with custom workflows and was forked from
 * `RecipientsSelectProvider` in order to support:
 * - entering arbitrary email addresses
 * - (future) choosing Role recipients.
 */
const RecipientSelectProvider: React.FC = ({ children }) => {
  const { projectId } = useProjectContext()
  const { data, loading, refetch } = useRecipientSelectRecipientsQuery({
    ...DefaultQueryHookOptions,
    variables: { globalId: projectId },
  })

  const userLabel = useUserSelectLabel()
  const userTooltip = useUserSelectTooltip()
  const userDeleted = useUserDeleted()
  function toValue({
    __typename,
    id,
  }: {
    id: string | number
    __typename?: 'User' | 'NonUserEmail'
  }) {
    return `${__typename}${SEPARATOR}${id}`
  }
  function label(r: UserSelectLabelUser | NonUserEmailOption) {
    if (r.__typename === 'User') return userLabel(r)
    return r.label ?? r.address
  }
  function tooltip(r: UserSelectTooltipUser | NonUserEmailOption) {
    if (r.__typename === 'User') return userTooltip(r)
    else if (!!r.label) return r.address
    else return undefined
  }
  function deleted(r: Recipient) {
    if (r.__typename === 'NonUserEmail') return undefined
    return userDeleted(r)
  }

  const users = useOptions({
    items: data?.project.users,
    value: toValue,
    label,
    tooltip,
    sort: (a, b) => alphabeticalCompare(label(a), label(b)),
  })
  const nonUserEmails = useOptions({
    items: data?.project.nonUserEmails,
    value: toValue,
    label,
    tooltip,
    sort: (a, b) => alphabeticalCompare(label(a), label(b)),
  })

  return (
    <Provider
      value={{
        refetch,
        loading,
        options: [
          { label: 'Users', options: users },
          { label: 'Non User Emails', options: nonUserEmails },
        ],
        findAddressFromValue: (value: LabeledValue['value']) => {
          if (!data) return undefined
          const [type, id] = (value as string).split(SEPARATOR)

          switch (type) {
            case 'User':
              return data.project.users.find((user) => user.id === id)?.mail
            case 'NonUserEmail':
              return data.project.nonUserEmails.find(
                (nUser) => nUser.id === parseInt(id)
              )?.address
            default:
              return undefined
          }
        },
        findOptionFromValue: (value: string | number) => {
          return [...users, ...nonUserEmails].find(
            (u) => u.value?.toString() === value.toString()
          )
        },
        findOptionFromAddress: (address: string) => {
          if (!data) return undefined
          const addr = address.toLowerCase()

          const user = data.project.users.find(
            (u) => u.mail.toLowerCase() === addr
          )
          if (user) return users.find((uOpt) => uOpt.value === toValue(user))

          const nonUserEmail = data.project.nonUserEmails.find(
            (nUser) => nUser.address.toLowerCase() === addr
          )
          if (nonUserEmail)
            return nonUserEmails.find(
              (nuOpt) => nuOpt.value === toValue(nonUserEmail)
            )

          return undefined
        },
        fromLabeledValues: (values) =>
          (values ?? []).map(({ value }) => {
            const [type, splitValue] = (value as string).split(SEPARATOR)

            switch (type) {
              case 'User':
                return { type: 'user', userId: splitValue } as const
              case 'NonUserEmail':
                return {
                  type: 'non_user_email',
                  nonUserEmailId: parseInt(splitValue),
                } as const
              default:
                return { type: 'address', value: value as string } as const
            }
          }),
        fromLabeledValue: function (value) {
          return value ? this.fromLabeledValues([value])[0] : undefined
        },
        toLabeledValues: (recipients) =>
          recipients
            ? toLabeledValues(recipients, {
                value: toValue,
                label,
                tooltip,
                deleted,
              })
            : [],
        toLabeledValue: (recipient) =>
          recipient
            ? toLabeledValues([recipient], {
                value: toValue,
                label,
                tooltip,
                deleted,
              })[0]
            : undefined,
      }}
    >
      {children}
    </Provider>
  )
}

export default RecipientSelectProvider
